import { useMemo } from 'react'
import { useRouter } from 'next/router'
import { uniq } from 'ramda'
import { getQueryAllergen } from '#src/products/product-category-page/get-products-by-category'

type UpdateFilterQueryParam = {
  type: string
  name: string
  action: string
}

export const useAllergensQuery = () => {
  const router = useRouter()
  const getActualPathName = () => {
    let pathname = router?.pathname
    const category = router?.query?.category
    const subcategory = router?.query?.subcategory

    if (pathname?.includes('category') && typeof category === 'string') {
      pathname = pathname?.replace('[category]', category)
    }

    if (pathname?.includes('subcategory') && typeof subcategory === 'string') {
      pathname = pathname?.replace('[subcategory]', subcategory)
    }

    return pathname
  }

  const updateFilterMultipleQuery = ({ allergens, suitables }) => {
    const pathname = getActualPathName()
    const queries = getQueryAllergen({ suitables, allergens })
    router.push(`${pathname}${queries}`)
  }

  const createListString = ({
    type,
    name,
    action,
    defaultList = null
  }): string => {
    const listFromQuery =
      defaultList !== null ? defaultList : router?.query?.[type] || ''

    if (typeof listFromQuery === 'string') {
      let listString = listFromQuery
      if (action === 'remove') {
        listString = listString
          ?.split(',')
          ?.filter(item => item !== name)
          ?.join(',')
      }
      if (action === 'add' && !listString?.includes(name)) {
        listString = listString?.length ? `${listString},${name}` : name
      }

      return listString
    }

    return ''
  }

  const createListStringFromList = ({
    type,
    names,
    action,
    defaultList = ''
  }): string => {
    const listFromQuery = defaultList || router?.query?.[type] || ''

    if (typeof listFromQuery === 'string') {
      const listName = names?.split(',')

      if (!listFromQuery?.length) {
        if (action === 'add') {
          return listName?.join(',')
        }
        return ''
      }

      let listString = listFromQuery?.split(',')
      if (action === 'remove') {
        listString = listString?.filter(item => !listName?.includes(item))
      } else if (action === 'add') {
        listString = uniq(listString?.concat(listName))
      }

      return listString?.join(',')
    }

    return ''
  }

  const updateFilterQuery = ({
    type,
    name,
    action
  }: UpdateFilterQueryParam) => {
    let allergens = router?.query?.allergens || ''
    let suitables = router?.query?.suitables || ''

    if (type === 'allergens') {
      const listString = createListString({ type, name, action })
      allergens = listString
    }

    if (type === 'suitables') {
      const listString = createListString({ type, name, action })
      suitables = listString
    }

    const pathname = getActualPathName()
    const queries = getQueryAllergen({ suitables, allergens })

    router.push(`${pathname}${queries}`)
  }

  const allergens = useMemo(() => {
    let allergensFromQuery = router?.query?.allergens
    if (typeof allergensFromQuery === 'string' && allergensFromQuery?.length) {
      return allergensFromQuery
    }

    return ''
  }, [router?.query?.allergens])

  const arrAllergens = useMemo(() => {
    if (allergens?.length) {
      return allergens?.split(',')
    }

    return []
  }, [allergens])

  const suitables = useMemo(() => {
    let suitablesFromQuery = router?.query?.suitables
    if (typeof suitablesFromQuery === 'string' && suitablesFromQuery?.length) {
      return suitablesFromQuery
    }

    return ''
  }, [router?.query?.suitables])

  const arrSuitables = useMemo(() => {
    if (suitables?.length) {
      return suitables?.split(',')
    }

    return []
  }, [suitables])

  return {
    updateFilterQuery,
    updateFilterMultipleQuery,
    createListString,
    createListStringFromList,
    arrSuitables,
    arrAllergens,
    suitables,
    allergens
  }
}
